import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { getThread } from '@/lib/api/threads'
import { rememberThreadWorkspaceMeta } from '@/lib/thread-workspace-meta'
import { isOfflineOrUnreachableFetchError } from '@/lib/api/core'

/** Resolve linked ai_conversation id + title for an agent_chat thread. */
export function useHostedChatThread(
  threadId: string | undefined,
  options?: {
    initialTitle?: string
    onTitleResolved?: (threadId: string, title: string) => void
    /** When GET resolves a canonical threads.id different from the requested id. */
    onThreadIdResolved?: (requestedId: string, canonicalThreadId: string) => void
  },
) {
  const [linkedConversationId, setLinkedConversationId] = useState<string | null>(null)
  /** Thread id that linkedConversationId / resolvedTitle belong to (avoids stale meta on switch). */
  const [linkedForThreadId, setLinkedForThreadId] = useState<string | null>(null)
  const [resolvedTitle, setResolvedTitle] = useState(options?.initialTitle ?? 'New chat')
  const [resolving, setResolving] = useState(Boolean(threadId && !threadId.startsWith('new-')))
  const loadGenRef = useRef(0)
  const metaReady = !threadId || threadId.startsWith('new-') || linkedForThreadId === threadId
  const effectiveLinkedConversationId = metaReady ? linkedConversationId : null
  const effectiveResolving =
    Boolean(threadId && !threadId.startsWith('new-') && !metaReady) || resolving
  const onTitleResolvedRef = useRef(options?.onTitleResolved)
  onTitleResolvedRef.current = options?.onTitleResolved
  const onThreadIdResolvedRef = useRef(options?.onThreadIdResolved)
  onThreadIdResolvedRef.current = options?.onThreadIdResolved

  useEffect(() => {
    if (options?.initialTitle) {
      setResolvedTitle(options.initialTitle)
    }
  }, [options?.initialTitle])

  useEffect(() => {
    if (!threadId || threadId.startsWith('new-')) {
      setLinkedConversationId(null)
      setLinkedForThreadId(null)
      setResolving(false)
      return
    }

    const gen = ++loadGenRef.current
    setResolving(true)

    void getThread(threadId)
      .then((thread) => {
        if (gen !== loadGenRef.current) return
        rememberThreadWorkspaceMeta(thread)
        setResolvedTitle(thread.title)
        onTitleResolvedRef.current?.(thread.id, thread.title)
        if (thread.id !== threadId) {
          onThreadIdResolvedRef.current?.(threadId, thread.id)
        }
        setLinkedForThreadId(threadId)
        setLinkedConversationId(thread.linkedConversationId ?? null)
      })
      .catch((err) => {
        if (gen !== loadGenRef.current) return
        if (isOfflineOrUnreachableFetchError(err)) return
        console.error('[useHostedChatThread] Failed to load thread:', err)
        toast.error('Could not load chat thread')
      })
      .finally(() => {
        if (gen === loadGenRef.current) {
          setResolving(false)
        }
      })
  }, [threadId])

  return {
    linkedConversationId: effectiveLinkedConversationId,
    resolvedTitle,
    resolving: effectiveResolving,
  }
}
